import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { computed, ref, watch } from 'vue';
import { PAGE_ERROR, PAGE_SENDING, PAGE_SUCCESS } from '../routes';
import { getHeader } from '../services/app-shell';
import * as logger from '../utils/logger';
import { useAuth } from './auth-store';
import { useProfile } from './profile-store';

const defaultHeaderModelFactory = () => ({
    headerAppearance: {
        transparent: false,
        white: false,
        minimal: false,
    },
    // Empty string initialization is necessary because the view model doesn't work as expected with null.
    user: {
        firstName: '',
        lastName: '',
        email: '',
        profileImageUrl: '',
    },
    status: {
        loggedIn: false,
        loading: false,
    },
    loginButton: {
        hidden: false,
    },
});

/**
 * `useHeaderStore` manages the micro frontend header state. It updates header content
 * automatically when the model changes, based on user and route information.
 * It also exposes methods to manually fetch the header.
 */
export const useHeaderStore = defineStore('header', () => {
    const route = useRoute();
    const auth = useAuth();
    const profile = useProfile();

    const html = ref('<div data-qa="header placeholder" class="l-header"/>');
    const model = computed(() => {
        const computedHeaderModel = defaultHeaderModelFactory();
        computedHeaderModel.status.loggedIn = auth.isLoggedIn;

        switch (route.name) {
        // Set minimal header for page_error and hide login button.
        case PAGE_ERROR:
            computedHeaderModel.headerAppearance.minimal = true;
            computedHeaderModel.loginButton.hidden = true;
            break;
        // Set normal header for page_success.
        case PAGE_SUCCESS:
            // `isLoggedIn` getter can be used because data is already in store.
            if (auth.isLoggedIn) {
                computedHeaderModel.user.email = profile.email;
                computedHeaderModel.status.loggedIn = true;

                if (profile.hasProfile) {
                    computedHeaderModel.user.profileImageUrl = profile.profileImageUrl;
                }

                if (profile.hasValidProfile) {
                    computedHeaderModel.user.firstName = profile.firstname;
                    computedHeaderModel.user.lastName = profile.lastname;
                }
            }
            computedHeaderModel.headerAppearance.minimal = true;
            break;
        case PAGE_SENDING:
            computedHeaderModel.headerAppearance.minimal = true;
            computedHeaderModel.loginButton.hidden = true;
            break;
        // Set default header to minimal because page_error isn't detected at every time.
        // URL can be / only.
        default:
            computedHeaderModel.headerAppearance.minimal = true;
            break;
        }

        return computedHeaderModel;
    });

    function updateHeader(headerResponseObject) {
        html.value = headerResponseObject.html;

        // Due to security reasons browsers don't execute scripts in SPAs,
        // therefore the app-shell sends the microfrontend header as JSON.
        // For more information see the app-shell README: https://gitlab.karriere.dev/microfrontends/app-shell
        headerResponseObject.scripts.forEach((script) => {
            const element = document.createElement('script');
            if (script.src) {
                element.src = script.src;
                element.async = true;
                element.defer = true;
            }
            if (document.body) {
                document.body.appendChild(element);
            }
        });
    }

    async function fetchHeader() {
        try {
            const response = await getHeader(model.value);
            updateHeader(response.data);
        } catch (exception) {
            logger.error({
                exception,
                message: 'Fetching the header failed.',
                extra: {
                    response:
                            exception.response && exception.response.data ?
                                JSON.stringify(exception.response.data) :
                                'No response available.',
                },
            });
        }
    }

    watch(model, fetchHeader);

    return {
        html,
        fetchHeader,
        updateHeader,
    };
});
